import { useMemo, useRef, useState } from "react";
import { useGallery, type GalleryImage } from "@/hooks/content";
import type { GalleryAlbum } from "@/mocks/cms";
import {
  PageHeader,
  ConfirmDialog,
  useToast,
  newId,
  StatusBadge,
  statusTone,
} from "./components/admin-ui";
import { uploadFileWithProgress, uniquePath } from "@/lib/supabase";

const categories = [
  "News",
  "Studio",
  "Events",
  "Programs",
  "Sports",
  "Behind the Scenes",
  "Community",
];

const emptyAlbum = { name: "", category: "", description: "" };

export default function GalleryAdminPage() {
  const { albums, images } = useGallery();
  const { show } = useToast();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [albumModalOpen, setAlbumModalOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState<GalleryAlbum | null>(null);
  const [albumForm, setAlbumForm] = useState(emptyAlbum);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [confirmAlbumId, setConfirmAlbumId] = useState<string | null>(null);
  const [confirmImageId, setConfirmImageId] = useState<string | null>(null);

  const selectedAlbum = albums.items.find((a) => a.id === selectedId) ?? null;
  const albumImages = useMemo(
    () =>
      images.items
        .filter((img) => img.albumId === selectedId)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [images.items, selectedId],
  );

  function openCreateAlbum() {
    setEditingAlbum(null);
    setAlbumForm(emptyAlbum);
    setAlbumModalOpen(true);
  }

  function openEditAlbum(a: GalleryAlbum) {
    setEditingAlbum(a);
    setAlbumForm({ name: a.name, category: a.category, description: a.description });
    setAlbumModalOpen(true);
  }

  async function saveAlbum() {
    if (!albumForm.name.trim()) {
      show("Album name is required.", "error");
      return;
    }
    if (editingAlbum) {
      await albums.update(editingAlbum.id, {
        name: albumForm.name.trim(),
        category: albumForm.category,
        description: albumForm.description,
      });
      show("Album updated.");
    } else {
      const album: GalleryAlbum = {
        id: newId(),
        name: albumForm.name.trim(),
        category: albumForm.category,
        description: albumForm.description,
        cover: "",
        status: "published",
      };
      await albums.add(album);
      show("Album created.");
    }
    setAlbumModalOpen(false);
  }

  async function handleImageUpload(files: FileList) {
    if (!selectedId || files.length === 0) return;
    setUploading(true);
    setProgress(0);

    try {
      const list = Array.from(files);
      let firstUrl = "";

      for (let i = 0; i < list.length; i++) {
        const file = list[i];
        const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")) : ".jpg";
        const url = await uploadFileWithProgress({
          bucket: "gallery",
          path: uniquePath("images", `photo-${i}${ext}`),
          file,
          onProgress: (p) => setProgress(Math.round(((i + p / 100) / list.length) * 100)),
        });
        if (!firstUrl) firstUrl = url;

        const img: GalleryImage = {
          id: newId(),
          albumId: selectedId,
          url,
          caption: file.name.replace(/\.[^.]+$/, ""),
          sortOrder: albumImages.length + i,
        };
        await images.add(img);
      }

      // Set the album cover to the first uploaded image if it has none yet.
      if (firstUrl && selectedAlbum && !selectedAlbum.cover) {
        await albums.update(selectedAlbum.id, { cover: firstUrl });
      }

      show(`${list.length} image${list.length > 1 ? "s" : ""} uploaded.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      show(e instanceof Error ? e.message : "Upload failed. Please try again.", "error");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  const confirmAlbum = albums.items.find((a) => a.id === confirmAlbumId) ?? null;
  const confirmImage = images.items.find((i) => i.id === confirmImageId) ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Gallery"
        subtitle="Create albums and upload photos shown to visitors on the website."
        actions={
          <button
            type="button"
            onClick={openCreateAlbum}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-sm" />
            </span>
            New Album
          </button>
        }
      />

      {albums.loading ? (
        <div className="bg-white rounded-lg border border-background-200 p-10 text-center text-sm text-foreground-500">
          Loading gallery…
        </div>
      ) : albums.items.length === 0 ? (
        <div className="bg-white rounded-lg border border-background-200 p-12 text-center">
          <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-image-line text-xl" />
          </div>
          <p className="mt-3 text-sm text-foreground-600">No albums yet. Create your first album.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {albums.items.map((a) => {
            const count = images.items.filter((img) => img.albumId === a.id).length;
            const active = a.id === selectedId;
            return (
              <div
                key={a.id}
                className={`bg-white rounded-lg border overflow-hidden transition-colors ${
                  active ? "border-primary-400 ring-2 ring-primary-100" : "border-background-200"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setSelectedId(a.id)}
                  className="w-full text-left block"
                >
                  <div className="relative h-36 bg-background-100 overflow-hidden">
                    {a.cover ? (
                      <img src={a.cover} alt={a.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-foreground-300">
                        <i className="ri-image-line text-2xl" />
                      </div>
                    )}
                    <span className="absolute top-2 left-2">
                      <StatusBadge tone={statusTone(a.status)}>{a.status}</StatusBadge>
                    </span>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-semibold text-foreground-950 truncate">{a.name}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {a.category || "Uncategorized"} · {count} photo{count !== 1 ? "s" : ""}
                    </p>
                  </div>
                </button>
                <div className="flex items-center justify-end gap-1 px-2 pb-2">
                  <button
                    type="button"
                    onClick={() => openEditAlbum(a)}
                    className="px-2.5 py-1.5 text-xs font-medium text-foreground-600 border border-background-200 rounded-md hover:bg-background-50 whitespace-nowrap"
                  >
                    <i className="ri-pencil-line" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmAlbumId(a.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-accent-50 text-accent-600"
                    aria-label="Delete album"
                  >
                    <i className="ri-delete-bin-line text-base" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Selected album detail */}
      {selectedAlbum && (
        <div className="bg-white rounded-lg border border-background-200 p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="text-base font-semibold text-foreground-950">{selectedAlbum.name}</h2>
              <p className="text-xs text-foreground-500 mt-0.5">{selectedAlbum.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-upload-cloud-line text-sm" />
                </span>
                Upload Images
              </button>
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="px-3 py-2 text-sm font-medium text-foreground-600 border border-background-200 rounded-md hover:bg-background-50 whitespace-nowrap"
              >
                Close
              </button>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
          />

          {uploading && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs text-foreground-600 mb-1.5">
                <span>Uploading images…</span>
                <span className="font-semibold">{progress}%</span>
              </div>
              <div className="h-2 bg-background-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {albumImages.length === 0 ? (
            <div className="text-center py-10 text-foreground-500">
              <i className="ri-image-line text-3xl text-foreground-300 mb-2" />
              <p className="text-sm">No images yet. Click “Upload Images” to add photos.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {albumImages.map((img) => (
                <div key={img.id} className="relative group rounded-md overflow-hidden bg-background-100">
                  <img src={img.url} alt={img.caption} className="w-full aspect-square object-cover" />
                  <button
                    type="button"
                    onClick={() => setConfirmImageId(img.id)}
                    className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-md bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Delete image"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Album modal */}
      {albumModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div className="fixed inset-0 bg-foreground-900/50" onClick={() => setAlbumModalOpen(false)} aria-hidden="true" />
          <div className="relative bg-white rounded-lg w-full max-w-lg my-8">
            <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-100">
              <h2 className="text-base font-semibold text-foreground-950">
                {editingAlbum ? "Edit Album" : "New Album"}
              </h2>
              <button
                type="button"
                onClick={() => setAlbumModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-500"
                aria-label="Close"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </header>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  Album name <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  value={albumForm.name}
                  onChange={(e) => setAlbumForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Album name"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">Category</label>
                <select
                  value={albumForm.category}
                  onChange={(e) => setAlbumForm((p) => ({ ...p, category: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  <option value="">Select…</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">Description</label>
                <textarea
                  value={albumForm.description}
                  onChange={(e) => setAlbumForm((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                  placeholder="Album description"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
            </div>
            <footer className="flex items-center justify-end gap-2 px-5 py-4 border-t border-background-100">
              <button
                type="button"
                onClick={() => setAlbumModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-foreground-700 border border-background-200 rounded-md hover:bg-background-50 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveAlbum}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 whitespace-nowrap"
              >
                Save Album
              </button>
            </footer>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmAlbumId !== null}
        title="Delete album?"
        message={
          confirmAlbum
            ? `Delete "${confirmAlbum.name}" and all its photos? This cannot be undone.`
            : ""
        }
        onConfirm={() => {
          if (confirmAlbumId) {
            albums.remove(confirmAlbumId);
            if (selectedId === confirmAlbumId) setSelectedId(null);
            show("Album deleted.");
          }
          setConfirmAlbumId(null);
        }}
        onCancel={() => setConfirmAlbumId(null)}
      />

      <ConfirmDialog
        open={confirmImageId !== null}
        title="Delete photo?"
        message="This photo will be removed from the album."
        onConfirm={() => {
          if (confirmImageId) {
            images.remove(confirmImageId);
            show("Photo deleted.");
          }
          setConfirmImageId(null);
        }}
        onCancel={() => setConfirmImageId(null)}
      />
    </div>
  );
}